import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { TellentNotesHttpClient } from './notes-http-client';
import { getTellentNoteScope, TellentNotesActions } from './notes.store';

@Injectable({ providedIn: 'root' })
export class TellentNotesEffects {
  private readonly tellentNotesHttpClient = inject(TellentNotesHttpClient);
  private readonly actions$ = inject(Actions);

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentNotesActions.fetchCollection),
      switchMap(({ payload }) =>
        this.tellentNotesHttpClient.loadNotes(payload.resourceType, payload.resourceId).pipe(
          mergeMap(notes => [
            TellentNotesActions.fetchCollectionSuccess({
              payload: {
                data: notes,
                scope: getTellentNoteScope(payload.resourceType, payload.resourceId),
              },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentNotesActions.fetchCollectionFailed({
                payload: {
                  error,
                  scope: getTellentNoteScope(payload.resourceType, payload.resourceId),
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public createNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentNotesActions.createOnePessimistic),
      switchMap(({ payload }) =>
        this.tellentNotesHttpClient
          .createNote(payload.resourceType, payload.resourceId, payload.note)
          .pipe(
            mergeMap(note => [
              TellentNotesActions.createOnePessimisticSuccess({
                payload: {
                  data: note,
                  scope: getTellentNoteScope(payload.resourceType, payload.resourceId),
                  correlationId: payload.correlationId,
                },
              }),
            ]),
            catchError((error: HttpErrorResponse) =>
              of(
                TellentNotesActions.createOnePessimisticFailed({
                  payload: { error, data: payload.note, correlationId: payload.correlationId },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public removeNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentNotesActions.removeOnePessimistic),
      switchMap(({ payload }) =>
        this.tellentNotesHttpClient.deleteNote(payload.id).pipe(
          mergeMap(() => [
            TellentNotesActions.removeOnePessimisticSuccess({
              payload: { data: { id: payload.id } },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentNotesActions.removeOnePessimisticFailed({
                payload: { error, data: payload.id },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentNotesActions.updateOnePessimistic),
      switchMap(({ payload }) =>
        this.tellentNotesHttpClient.updateNote(payload.id, payload.note).pipe(
          mergeMap(note => [
            TellentNotesActions.updateOnePessimisticSuccess({
              payload: { data: note },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentNotesActions.updateOnePessimisticFailed({
                payload: { error, data: { id: payload.id } },
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
